// src/components/TeamSelector.js
import React, { useState, useEffect } from 'react';
import { useNotify, useRefresh, useDataProvider } from 'react-admin';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Chip,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import TeamIcon from '@mui/icons-material/PeopleAlt';
import InfoIcon from '@mui/icons-material/Info';
import { useQueryClient, useQuery } from 'react-query';

const TeamSelector = () => {
  const [open, setOpen] = useState(false);
  const [currentTeam, setCurrentTeam] = useState('');
  const [currentTeamName, setCurrentTeamName] = useState('Select Team');
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider();
  const userRole = localStorage.getItem('userRole');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch teams directly from API
  const { data: teams, isLoading, refetch } = useQuery(
    ['teams'],
    async () => {
      const result = await dataProvider.getList('teams', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' }
      });
      return result.data;
    },
    {
      enabled: userRole === 'tenant_admin'
    }
  );
  
  // Always use useEffect, but with conditional logic inside
  useEffect(() => {
    if (userRole !== 'tenant_admin') return; // Skip for non-admin users

    const teamId = localStorage.getItem('userTeamId');
    setCurrentTeam(teamId);

    // Find the current team in the list and get its name
    if (teamId && teams) {
      const team = teams.find(t => t.id === teamId);
      if (team) {
        setCurrentTeamName(team.name);
        // Also store the team name in localStorage for other components
        localStorage.setItem('teamName', team.name);
      }
    }
  }, [teams, userRole]);

  const handleOpen = () => {
    setOpen(true);
    // Refresh teams list when opening
    refetch();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const switchTeam = (teamId, teamName) => {
    localStorage.setItem('userTeamId', teamId);
    localStorage.setItem('teamName', teamName);
    setCurrentTeam(teamId);
    setCurrentTeamName(teamName);
    setOpen(false);
    notify(`Switched to team: ${teamName}`, { type: 'success' });
    
    // Invalidate queries that depend on teamId to force refetch
    queryClient.invalidateQueries('dashboardCounts');
    queryClient.invalidateQueries(['voipNumber']);
    queryClient.invalidateQueries(['recentCalls']);
    queryClient.invalidateQueries(['dashboardSettings']);
    queryClient.invalidateQueries(['onDutyContact']);
    queryClient.invalidateQueries(['calendarStatus']);
    
    // Refresh the interface
    refresh();
  };
  
  // Only render UI for tenant_admin role
  if (userRole !== 'tenant_admin') {
    return null;
  }

  return (
    <>
      {isSmallScreen ? (
        <IconButton
          color="inherit"
          onClick={handleOpen}
          size="small"
          sx={{ mr: 1 }}
        >
          <TeamIcon />
        </IconButton>
      ) : (
        <Button
          color="inherit"
          startIcon={<TeamIcon />}
          onClick={handleOpen}
          style={{ marginRight: 8 }}
          variant="outlined"
          size="small"
        >
          {currentTeamName}
        </Button>
      )}
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <TeamIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Select Team</Typography>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <CircularProgress />
            </Box>
          ) : (
            teams && teams.length > 0 ? (
              <>
                <Typography variant="body2" color="textSecondary" paragraph>
                  You can manage multiple teams. Click on a team to select it.
                </Typography>
                <List>
                  {teams.map(team => (
                    <ListItem 
                      key={team.id}
                      selected={team.id === currentTeam}
                      onClick={() => team.id !== currentTeam && switchTeam(team.id, team.name)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: team.id === currentTeam ? '1px solid' : 'none',
                        borderColor: 'primary.main',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        },
                        '&:hover': {
                          backgroundColor: team.id === currentTeam ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.08)',
                          cursor: 'pointer'
                        },
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <ListItemText 
                        primary={team.name} 
                        secondary={`Team ID: ${team.id}`} 
                      />
                      {team.id === currentTeam && (
                        <Chip 
                          label="Current" 
                          color="primary" 
                          size="small"
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                <InfoIcon color="warning" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="body1" color="textSecondary" align="center">
                  No teams available
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                  Go to Teams management to create a new team
                </Typography>
              </Box>
            )
          )}
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamSelector;